import { useEffect } from "react";
import { match } from "ts-pattern";

import { Box, Stack, Title } from "metabase/ui";
import type {
  InspectorCard,
  InspectorSection,
  InspectorSectionId,
  InspectorSource,
  InspectorVisitedFields,
} from "metabase-types/api";

import { useLensContentContext } from "../../LensContent/LensContentContext";

import { ComparisonLayout } from "./components/ComparisonLayout/ComparisonLayout";
import { FlatLayout } from "./components/FlatLayout";

type DefaultLensSectionsProps = {
  sections: InspectorSection[];
  cardsBySection: Record<InspectorSectionId, InspectorCard[]>;
  sources: InspectorSource[];
  visitedFields?: InspectorVisitedFields;
};

export const DefaultLensSections = ({
  sections,
  cardsBySection,
  sources,
  visitedFields,
}: DefaultLensSectionsProps) => {
  const { markCardStartedLoading, lens } = useLensContentContext();

  /**
   * Mark all cards as loading because the underlying components use useProgressiveLoader.
   * Without this, useCardLoadingTracker emits an event for each batch instead of only when all cards are loaded.
   */
  useEffect(() => {
    lens?.cards.forEach((card) => markCardStartedLoading(card.id));
  }, [lens, markCardStartedLoading]);

  return (
    <Box>
      {sections.map((section) => {
        const cards = cardsBySection[section.id] ?? [];

        if (cards.length === 0) {
          return null;
        }

        const content = match(section.layout ?? "flat")
          .with("comparison", () => (
            <ComparisonLayout
              cards={cards}
              sources={sources}
              visitedFields={visitedFields}
            />
          ))
          .with("flat", () => <FlatLayout cards={cards} />)
          .exhaustive();

        return (
          <Stack key={section.id} gap="md">
            <Title order={3}>{section.title}</Title>
            {content}
          </Stack>
        );
      })}
    </Box>
  );
};
